import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { formatCurrency, formatDate, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { ClipboardCheck, TrendingUp, ArrowDownUp, PackageSearch } from "lucide-react";

export default function InventoryPage() {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [counts, setCounts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [valuation, setValuation] = useState(null);
  const [counted, setCounted] = useState({});
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    try {
      const [pRes, cRes, mRes, vRes] = await Promise.all([
        axios.get(`${API_URL}/products`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/inventory/counts`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/stock/movements`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/stock/valuation`, { headers, withCredentials: true }),
      ]);
      setProducts(pRes.data.filter((p) => p.track_stock));
      setCounts(cRes.data);
      setMovements(mRes.data);
      setValuation(vRes.data);
    } catch { toast.error("Erro ao carregar inventário"); }
    finally { setLoading(false); }
  };

  const applyCount = async () => {
    const items = Object.entries(counted)
      .filter(([, v]) => v !== "" && v != null)
      .map(([product_id, v]) => ({ product_id, counted_qty: parseFloat(v) }));
    if (items.length === 0) return toast.error("Introduza pelo menos uma contagem");
    if (!window.confirm(`Aplicar inventário a ${items.length} produto(s)? O stock será ajustado.`)) return;
    try {
      const res = await axios.post(`${API_URL}/inventory/counts`, { items, notes }, { headers, withCredentials: true });
      toast.success(`Inventário ${res.data.number} aplicado (${res.data.adjustments} ajuste(s))`);
      setCounted({}); setNotes("");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao aplicar inventário"); }
  };

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div>
        <h1 className="text-3xl font-bold font-['Outfit']">Inventário</h1>
        <p className="text-muted-foreground">Contagem física, valorização e movimentos de stock</p>
      </div>

      {valuation && (
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Unidades em Stock</p>
            <p className="text-xl font-bold" data-testid="valuation-units">{valuation.total_units}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Valor de Custo</p>
            <p className="text-xl font-bold font-mono">{formatCurrency(valuation.total_cost_value)}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Valor de Venda</p>
            <p className="text-xl font-bold font-mono">{formatCurrency(valuation.total_sale_value)}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Lucro Potencial</p>
            <p className="text-xl font-bold font-mono text-emerald-600">{formatCurrency(valuation.potential_profit)}</p>
          </CardContent></Card>
        </div>
      )}

      <Tabs defaultValue="count">
        <TabsList>
          <TabsTrigger value="count" data-testid="tab-count"><ClipboardCheck className="w-4 h-4 mr-1" /> Contagem Física</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history"><PackageSearch className="w-4 h-4 mr-1" /> Inventários</TabsTrigger>
          <TabsTrigger value="movements" data-testid="tab-movements"><ArrowDownUp className="w-4 h-4 mr-1" /> Movimentos</TabsTrigger>
        </TabsList>

        <TabsContent value="count" className="space-y-3 mt-4">
          {loading ? <p className="text-muted-foreground">A carregar...</p> : products.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-muted-foreground">Nenhum produto com controlo de stock</CardContent></Card>
          ) : (
            <>
              <Card>
                <CardContent className="p-0 divide-y">
                  {products.map((p) => (
                    <div key={p.product_id} className="flex items-center justify-between p-3 gap-3">
                      <div className="flex-1">
                        <p className="font-medium">{p.name}</p>
                        <p className="text-xs text-muted-foreground">Stock no sistema: <b>{p.current_stock}</b> {p.stock_unit || "un"}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number" step="0.01" min="0"
                          className="w-28"
                          placeholder="Contado"
                          value={counted[p.product_id] ?? ""}
                          onChange={(e) => setCounted({ ...counted, [p.product_id]: e.target.value })}
                          data-testid={`count-input-${p.product_id}`}
                        />
                        {counted[p.product_id] !== "" && counted[p.product_id] != null && (
                          <Badge variant="outline" className={parseFloat(counted[p.product_id]) - p.current_stock === 0 ? "" : parseFloat(counted[p.product_id]) - p.current_stock > 0 ? "text-emerald-600" : "text-red-600"}>
                            {(parseFloat(counted[p.product_id]) - p.current_stock > 0 ? "+" : "") + (parseFloat(counted[p.product_id]) - p.current_stock).toFixed(2)}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
              <div className="flex gap-3 items-center">
                <Input placeholder="Notas do inventário (opcional)" value={notes} onChange={(e) => setNotes(e.target.value)} className="max-w-sm" />
                <Button onClick={applyCount} data-testid="apply-count-btn">
                  <ClipboardCheck className="w-4 h-4 mr-2" /> Aplicar Inventário
                </Button>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="history" className="space-y-3 mt-4">
          {counts.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-muted-foreground">Nenhum inventário realizado</CardContent></Card>
          ) : counts.map((c) => (
            <Card key={c.count_id}>
              <CardContent className="p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-mono font-bold">{c.number}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(c.created_at)} · {c.created_by_name} · {c.items.length} produto(s) · {c.adjustments} ajuste(s)</p>
                  </div>
                </div>
                <div className="mt-2 text-sm space-y-1">
                  {c.items.filter((i) => i.difference !== 0).map((i, idx) => (
                    <p key={idx} className="text-muted-foreground">
                      {i.product_name}: {i.system_qty} → <b>{i.counted_qty}</b>{" "}
                      <span className={i.difference > 0 ? "text-emerald-600" : "text-red-600"}>({i.difference > 0 ? "+" : ""}{i.difference})</span>
                    </p>
                  ))}
                  {c.items.filter((i) => i.difference !== 0).length === 0 && <p className="text-muted-foreground">Sem diferenças</p>}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="movements" className="mt-4">
          <Card>
            <CardContent className="p-0 divide-y max-h-[600px] overflow-y-auto">
              {movements.length === 0 && <p className="p-6 text-center text-muted-foreground">Sem movimentos</p>}
              {movements.map((m) => (
                <div key={m.movement_id} className="flex items-center justify-between p-3 text-sm">
                  <div>
                    <p className="font-medium">{m.product_name}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(m.created_at)} · {m.reason} · {m.created_by_name}</p>
                  </div>
                  <div className="text-right">
                    <Badge className={m.movement_type === "in" ? "bg-emerald-500/20 text-emerald-600" : "bg-red-500/20 text-red-600"}>
                      {m.movement_type === "in" ? "+" : "-"}{m.quantity}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">{m.stock_before} → {m.stock_after}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
